






































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import { ValidationObserverInstance, ValidationProvider } from "vee-validate";
import { AttributesHelper } from "../../lib/utils/AttributesHelper";

/**
 * This component is a advanced version of the simple HTML input tag. This component is used for user input via a textbox.
 */
@Component({
  components: {
    ValidationProvider
  }
})
export default class AnxInput extends Vue {
  /** This is the name of the input field */
  @Prop({ default: "anx-input" }) name!: string;
  /** This will be shown as field name in error messages. Can be used for localization */
  @Prop({ default: null }) dataVvAs!: string | null;
  /** This is the id of the input field. A unqiue id will be created if this attribute is unset */
  @Prop({ default: null }) id!: string | null;
  /** This is the label of the input field that will be displayed */
  @Prop({ default: "Input" }) label!: string;
  /** This property will be directly translated to HTML input type of the input field */
  @Prop({ default: "text" }) type!: string;
  /** These are the rules for validation. See [VeeValidate Rules](https://vee-validate.logaretm.com/v2/guide/rules.html). */
  @Prop({ default: null }) rules!: string | null;
  /** The width of the input field */
  @Prop({ default: "100%" }) width!: string;
  /**
   * This is the assisitive text that will be displayed underneath
   * the input field. Will only be shown, if there are no errors.
   */
  @Prop({ default: null }) assistiveText!: string | null;
  /**
   * If this property is set there will be no validation and you
   * can't change the value. The style and the behaviour of the
   * input will remain the same. The value can still be changed
   * programmatically via the v-model.
   */
  @Prop({ default: null }) readonly!: boolean | null;
  /** this is the value that can be used via **v-model** */
  @Prop({ default: "" }) value!: string;
  /** Display the input field inline */
  @Prop({ default: null }) inline!: boolean | null;
  /** HTML autocomplete attribute */
  @Prop({ default: "" }) autocomplete!: string;
  /** With this property you can set the input field to disabled */
  @Prop({ default: null }) disabled!: boolean | null;

  private active = false;
  private filled = false;

  /** The id that is actually used */
  private inputId: string | undefined = "";

  /** This variable is used as v-model for the input field (Using the value variable directly is not allowed because props should not be mutated directly) */
  private localValue = "";

  /**
   * Watch the localValue variable used as v-model for the input field
   * When it changes, the users is typing and changed some text, it has
   * to be checked if the input field should be active or not.
   */
  @Watch("localValue")
  localValueChanged(newVal: string) {
    if (newVal && newVal.length) {
      this.active = true;
    }
  }

  /**
   * Watch the attribute/prop value.
   * When this variable changes it means that the value for the input field has been
   * updated from the parent component programmatically. The localValue variable has to
   * be assigned and it has to be checked, if the input field is filled
   */
  @Watch("value")
  valueChanged() {
    this.localValue = this.value !== null ? this.stringValue : "";
    this.isFilled();
  }

  /**
   * Set the id for the component when created
   */
  private created() {
    this.inputId = AttributesHelper.attributes(this, {
      uniqueId: true,
      uniqueIdPrefix: "anx-input-"
    }).id;
  }

  /**
   * After mount, the prop value has to be assigned to the localValue and it has to be checked,
   * if the input field should be filled
   */
  private mounted() {
    this.localValue = this.value !== null ? this.stringValue : "";
    this.isFilled();
  }

  /** Do a validation if the component is updated */
  private updated() {
    /** Only validate, if the value is set */
    if (this.value && this.value !== "") this.validate();
  }

  /** This returns the string value of the value from the v-model */
  get stringValue() {
    if (typeof this.value === "string") {
      return this.value;
    }

    return String(this.value);
  }

  get cssProps() {
    return {
      "--input-width": this.width
    };
  }

  /** Check if the input-field is filled, Set class filled. */
  protected isFilled() {
    if (this.localValue && this.localValue.length > 0) {
      this.filled = true;
    } else {
      this.filled = false;
    }
  }

  /**When the User click in and out of the field, the state active will be set and
   * the state filled will be checked.
   */
  protected inputBlur() {
    /** Do validation on focus lost */
    this.validate();

    if (this.readonly === null) {
      this.active = !this.active;
      this.isFilled();
    }
  }

  /** Validate the input with the defined rules */
  private async validate() {
    if (this.rules !== null) {
      await (this.$refs.observer as ValidationObserverInstance).validate();
    }
  }

  /**
   * This function is called, when the user clicks the input field
   * If the field is readonly, nothing should happen
   */
  private clickInputField() {
    if (this.readonly === null) {
      this.active = true;
    }
  }
}
